import React from 'react';
import { getRem } from 'lib/core';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { SliderControl, SliderInput, SliderLabel, SliderHelperText } from '../elements';

const StyledWrapper = styled.div`
  display: flex;
  margin: ${getRem('2px')} 0;
`;

const StyledContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding-left: ${theme.font.size.caption.value};
  `}
`;

const Slider = ({
  ariaLabel = 'Slider',
  className,
  dataTestId = '',
  helperLabel = 'Helper Label',
  hideCurrentInput = false,
  hideHelperLabel = false,
  hideMaxVal = false,
  hideMinVal = false,
  hideSliderLabel = false,
  id,
  isDisabled = false,
  isInputReadOnly = false,
  max = 100,
  min = 0,
  name,
  onChange,
  sliderLabel = 'Slider Label',
  step = 1,
  value,
  ...other
}) => {
  const sanitizeValue = (value) => {
    if (value > max) {
      return max;
    } else if (value < min) {
      return min;
    } else if (min === max) {
      return min;
    } else {
      return value;
    }
  };
  return (
    <StyledContainer aria-label={ariaLabel} className={className} data-testid={dataTestId}>
      {!hideSliderLabel && (
        <SliderLabel dataTestId={dataTestId} className={className ? `${className}-label` : undefined}>
          {sliderLabel}
        </SliderLabel>
      )}
      <StyledWrapper className={className ? `${className}-wrapper` : undefined}>
        <SliderControl
          className={className ? `${className}-control` : undefined}
          value={sanitizeValue(Number(value))}
          {...{ dataTestId, hideMaxVal, hideMinVal, id, isDisabled, max, min, name, onChange, step }}
          {...other}
        />
        {!hideCurrentInput && (
          <SliderInput
            className={className ? `${className}-input` : undefined}
            disabled={isDisabled}
            label={sliderLabel}
            {...{ dataTestId, id, isInputReadOnly, max, min, name, onChange, value }}
          />
        )}
      </StyledWrapper>
      {!hideHelperLabel && (
        <SliderHelperText className={className ? `${className}-helper-label` : undefined} dataTestId={dataTestId}>
          {helperLabel}
        </SliderHelperText>
      )}
    </StyledContainer>
  );
};

Slider.propTypes = {
  /** Informs screen reader users what actions they should take */
  ariaLabel: PropTypes.string,
  /** Adds additional class to the component */
  className: PropTypes.string,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Text to be displayed as a helper text near the input field */
  helperLabel: PropTypes.string,
  /** If true, current input is hidden */
  hideCurrentInput: PropTypes.bool,
  /** If true, helper label is hidden */
  hideHelperLabel: PropTypes.bool,
  /** If true, max value is hidden */
  hideMaxVal: PropTypes.bool,
  /** If true, min value is hidden */
  hideMinVal: PropTypes.bool,
  /** If true, slider label is hidden */
  hideSliderLabel: PropTypes.bool,
  /** Identifier of the input component */
  id: PropTypes.string.isRequired,
  /** If true, input is disabled and value of it cannot be edited */
  isDisabled: PropTypes.bool,
  /** If true, input is in read only state, value cannot be edited */
  isInputReadOnly: PropTypes.bool,
  /** Maximum value in the range of permitted values */
  max: PropTypes.number,
  /** Minimum value in the range of permitted values */
  min: PropTypes.number,
  /** Name of the input */
  name: PropTypes.string.isRequired,
  /** Callback to be called when input's value is being changed by user interaction */
  onChange: PropTypes.func.isRequired,
  /** Text to be displayed as a slider text near the input field */
  sliderLabel: PropTypes.string,
  /** The granularity with which the slider can step through values */
  step: PropTypes.number,
  /** Current value of the slider */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export { Slider };
